import { useState } from 'react';
import { NavLink, useLocation, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  LayoutDashboard,
  Menu,
  ChevronDown,
  LogOut,
  FileText,
  Clock,
  DollarSign,
  User,
  PenTool,
  X,
  ClipboardList,
} from 'lucide-react';
import logoUrgSalud from '@/assets/logo-urgsalud-transparent.png';

const tensNavItems = [
  { label: 'Inicio', href: '/tens', icon: LayoutDashboard },
  { label: 'Documentos', href: '/tens/documentos', icon: FileText },
  { label: 'Firmar Contratos', href: '/tens/firmar', icon: PenTool },
  { label: 'Asistencia', href: '/tens/asistencia', icon: Clock },
  { label: 'Informes', href: '/tens/informes', icon: ClipboardList },
  { label: 'Mis Pagos', href: '/tens/pagos', icon: DollarSign },
  { label: 'Mi Perfil', href: '/tens/perfil', icon: User },
];

const mobileNavItems = [
  { label: 'Inicio', href: '/tens', icon: LayoutDashboard },
  { label: 'Asistencia', href: '/tens/asistencia', icon: Clock },
  { label: 'Documentos', href: '/tens/documentos', icon: FileText },
  { label: 'Perfil', href: '/tens/perfil', icon: User },
];

export default function TensLayout({ children }: { children: React.ReactNode }) {
  const { profile, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const initials = profile
    ? `${profile.nombre?.[0] || ''}${profile.apellido?.[0] || ''}`.toUpperCase()
    : 'T';

  return (
    <div className="min-h-screen bg-background">
      {/* Top bar */}
      <header className="sticky top-0 z-40 border-b border-border bg-card/95 backdrop-blur-md">
        <div className="flex items-center justify-between h-14 px-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setSidebarOpen(true)}>
              <Menu className="w-5 h-5" />
            </Button>
            <Link to="/tens"><img src={logoUrgSalud} alt="UrgSalud" className="h-8" /></Link>
            <Badge variant="outline" className="hidden sm:flex text-xs font-medium border-primary/30 text-primary">
              Portal TENS
            </Badge>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="gap-2">
                <Avatar className="w-7 h-7">
                  <AvatarImage src={profile?.avatar_url || undefined} />
                  <AvatarFallback className="text-xs bg-primary/10 text-primary">{initials}</AvatarFallback>
                </Avatar>
                <span className="hidden sm:inline text-sm font-medium truncate max-w-[120px]">
                  {profile?.nombre}
                </span>
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={() => navigate('/tens/perfil')}>
                <User className="w-4 h-4 mr-2" /> Mi Perfil
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={signOut} className="text-destructive">
                <LogOut className="w-4 h-4 mr-2" /> Cerrar Sesión
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar — desktop */}
        <aside className="hidden md:flex flex-col w-56 border-r border-border bg-card min-h-[calc(100vh-3.5rem)] p-3 gap-1">
          {tensNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.href;
            return (
              <NavLink key={item.href} to={item.href}>
                <div className={cn(
                  'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                )}>
                  <Icon className="w-4 h-4 shrink-0" />
                  {item.label}
                </div>
              </NavLink>
            );
          })}
        </aside>

        {/* Mobile sidebar overlay */}
        <AnimatePresence>
          {sidebarOpen && (
            <>
              <motion.div
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/40 z-50 md:hidden"
                onClick={() => setSidebarOpen(false)}
              />
              <motion.aside
                initial={{ x: -280 }} animate={{ x: 0 }} exit={{ x: -280 }}
                transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                className="fixed inset-y-0 left-0 w-72 bg-card border-r border-border z-50 p-4 md:hidden"
              >
                <div className="flex items-center justify-between mb-6">
                  <Link to="/tens"><img src={logoUrgSalud} alt="UrgSalud" className="h-8" /></Link>
                  <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(false)}>
                    <X className="w-5 h-5" />
                  </Button>
                </div>
                <nav className="space-y-1">
                  {tensNavItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.href;
                    return (
                      <NavLink key={item.href} to={item.href} onClick={() => setSidebarOpen(false)}>
                        <div className={cn(
                          'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                          isActive
                            ? 'bg-primary/10 text-primary'
                            : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                        )}>
                          <Icon className="w-4 h-4 shrink-0" />
                          {item.label}
                        </div>
                      </NavLink>
                    );
                  })}
                </nav>
              </motion.aside>
            </>
          )}
        </AnimatePresence>

        {/* Main content */}
        <main className="flex-1 p-4 md:p-6 pb-20 md:pb-6 max-w-5xl mx-auto w-full">
          {children}
        </main>
      </div>

      {/* Mobile bottom nav */}
      <div className="fixed bottom-0 left-0 right-0 z-40 md:hidden border-t border-border bg-card/95 backdrop-blur-md">
        <div className="flex items-center justify-around h-14">
          {mobileNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.href;
            return (
              <NavLink key={item.href} to={item.href} className="flex flex-col items-center gap-0.5 py-1.5 px-3">
                <Icon className={cn('w-5 h-5', isActive ? 'text-primary' : 'text-muted-foreground')} />
                <span className={cn('text-[10px] font-medium', isActive ? 'text-primary' : 'text-muted-foreground')}>
                  {item.label}
                </span>
              </NavLink>
            );
          })}
        </div>
      </div>
    </div>
  );
}
